/**
 * Iris test suites — the regression harness for the AI service agent.
 *
 * A suite holds up to 50 *scenarios*. A scenario is a script of customer turns:
 * an opening message plus the follow-ups to send once Iris replies. One turn is
 * just a question; four turns is a whole booking flow. Running a suite replays
 * every scenario against the tenant's *current* Iris config on a throwaway
 * simulator conversation, so nothing reaches a real customer.
 *
 * Grading is two-layered:
 * - **Assertions** decide PASS/FAIL automatically ("did `reserve_and_book` fire?").
 *   A scenario with no assertions is UNGRADED — nothing was checked.
 * - **Ratings** (Good / Acceptable / Poor + forced root cause) are the human's
 *   quality read on top.
 *
 * Every replay creates an immutable *run*. Definitions live on the scenario,
 * results live on the run, so a re-run can never leave a stale rating attached
 * to a fresh answer — and two runs can be compared to catch a regression.
 *
 * Reached through the operations proxy at `/api/messaging/iris/batch-tests`
 * (operations is the FE's auth boundary + RBAC gate; it forwards the
 * ai-operations JSON unchanged), mirroring the sibling `review.ts` /
 * `receptionists.ts` hooks. ai-operations is the source of truth for the wire
 * shape; the hooks below normalize it into flat display types.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiDelete, apiGet, apiPost, apiPut, operationsClient } from '@/lib/api';

const BASE = '/api/messaging/iris/batch-tests';

/** Max scenarios a single suite can hold (Fin parity). */
export const MAX_BATCH_QUESTIONS = 50;
/** Max customer turns in one scenario, including the opener. */
export const MAX_SCENARIO_TURNS = 20;
/** Max assertions on one scenario. */
export const MAX_ASSERTIONS = 10;

/**
 * Lifecycle of a suite or a run — mirrors the ai-operations `BatchTestStatus`
 * enum exactly (do not rename: these are the values the API returns).
 */
export type BatchTestStatus =
  /** Created, scenarios assembled, never run. */
  | 'PENDING'
  /** A replay is in progress. */
  | 'RUNNING'
  /** The replay finished. */
  | 'DONE'
  /** The replay hit an error. */
  | 'FAILED';

/** Automatic grade from a scenario's assertions. UNGRADED = none declared. */
export type ScenarioVerdict = 'UNGRADED' | 'PASS' | 'FAIL';

/**
 * Human quality rating of an answer — mirrors the ai-operations `BatchRating`
 * enum. `UNRATED` is the default until an operator grades it.
 */
export type ItemRating = 'UNRATED' | 'GOOD' | 'ACCEPTABLE' | 'POOR';

/**
 * Forced root-cause tags for a Poor answer — Fin's fixed taxonomy. These are the
 * exact string values the ai-operations API validates against (`@IsIn`), so the
 * value IS the human-readable label.
 */
export const BATCH_ROOT_CAUSES = [
  'Wrong/missing content',
  "Didn't clarify",
  'Used content incorrectly',
  'Tone wrong',
  'Answer length',
  'Other',
] as const;

/** Root-cause tag required whenever a scenario run is rated Poor. */
export type RootCause = (typeof BATCH_ROOT_CAUSES)[number];

/** Where a suite's scenarios came from (create payload only). */
export type BatchSource = 'recent' | 'conversations' | 'manual';

// ── Assertions ───────────────────────────────────────────────────────────────

/** What an assertion checks. Mirrors ai-operations' `ASSERTION_KINDS`. */
export type AssertionKind =
  | 'TOOL_CALLED'
  | 'TOOL_NOT_CALLED'
  | 'TOOL_SUCCEEDED'
  | 'TOOL_FAILED'
  | 'REPLY_CONTAINS'
  | 'REPLY_NOT_CONTAINS'
  | 'NO_ESCALATION'
  | 'ESCALATED'
  | 'ALL_GROUNDED';

/** Display metadata per assertion kind — label, whether it takes a value, and
 *  what that value means. Drives the assertion editor's dropdown + placeholder. */
export const ASSERTION_KIND_META: Record<
  AssertionKind,
  { label: string; valueLabel?: string; placeholder?: string }
> = {
  // Ordered so the outcome check sits first: "did the booking work" is almost
  // always what you mean, and asserting merely that the tool was *attempted*
  // passes on a flow that failed on every attempt.
  TOOL_SUCCEEDED: {
    label: 'Tool succeeded',
    valueLabel: 'Tool name',
    placeholder: 'reserve_and_book',
  },
  TOOL_CALLED: {
    label: 'Called tool (even if it failed)',
    valueLabel: 'Tool name',
    placeholder: 'check_availability',
  },
  TOOL_FAILED: {
    label: 'Tool failed',
    valueLabel: 'Tool name',
    placeholder: 'reserve_and_book',
  },
  TOOL_NOT_CALLED: {
    label: "Didn't call tool",
    valueLabel: 'Tool name',
    placeholder: 'collect_payment',
  },
  REPLY_CONTAINS: {
    label: 'Reply contains',
    valueLabel: 'Text',
    placeholder: 'booked',
  },
  REPLY_NOT_CONTAINS: {
    label: "Reply doesn't contain",
    valueLabel: 'Text',
    placeholder: '$89',
  },
  NO_ESCALATION: { label: 'Never escalated' },
  ESCALATED: { label: 'Escalated to a human' },
  ALL_GROUNDED: { label: 'Every reply grounded' },
};

/** The kinds an operator can pick, in menu order. */
export const ASSERTION_KINDS = Object.keys(ASSERTION_KIND_META) as AssertionKind[];

/** True when this kind is meaningless without a `value`. */
export function assertionNeedsValue(kind: AssertionKind): boolean {
  return ASSERTION_KIND_META[kind].valueLabel !== undefined;
}

/** One declared check on a scenario. */
export interface ScenarioAssertion {
  kind: AssertionKind;
  /** Tool name or substring, depending on `kind`. Absent for the valueless kinds. */
  value?: string;
}

/** The outcome of one declared check after a run. */
export interface AssertionResult extends ScenarioAssertion {
  passed: boolean;
  /** Human-readable "why" — what we looked for and what we actually saw. */
  detail: string;
}

// ── Personas & judging ───────────────────────────────────────────────────────

/** How the customer side of a scenario is produced. */
export type ScenarioMode = 'SCRIPTED' | 'SIMULATED';

/**
 * The customer an LLM plays in SIMULATED mode. A script proves one wording
 * works; a persona covers the variation real customers produce.
 */
export interface ScenarioPersona {
  description: string;
  goal: string;
  tone?: string;
  language?: string;
}

/** Hard ceiling on simulated customer turns. */
export const MAX_SIMULATED_TURNS = 15;

// ── Transcript ───────────────────────────────────────────────────────────────

export type TranscriptRole = 'customer' | 'iris';

/**
 * Whether a tool call worked. `unknown` means its result could not be read —
 * which satisfies neither TOOL_SUCCEEDED nor TOOL_FAILED, so an unreadable
 * result fails the assertion rather than quietly passing it.
 */
export type ToolStatus = 'ok' | 'error' | 'unknown';

/** One tool the engine invoked, and whether it actually worked. */
export interface ToolOutcome {
  name: string;
  status: ToolStatus;
  /** The tool's own error message, or why the result could not be read. */
  error?: string;
}

export interface TranscriptTurn {
  role: TranscriptRole;
  content: string;
  /** Tool names invoked while producing this turn (Iris turns only). */
  tools?: string[];
  /** Present only when a tool on this turn did NOT succeed — a tool can be
   *  called and still fail, which `tools` alone cannot show. */
  toolOutcomes?: ToolOutcome[];
  /** Engine's grounding flag for this turn, when it recorded one. */
  grounded?: boolean;
  /** True when this Iris turn escalated to a human. */
  escalated?: boolean;
}

// ── Wire shapes (ai-operations is the source of truth) ───────────────────────

/** Tallies over one run's scenario results. */
export interface RunSummary {
  total: number;
  answered: number;
  unanswered: number;
  passed: number;
  failed: number;
  ungraded: number;
  ratings: Record<ItemRating, number>;
}

/** One execution of a whole suite. */
export interface BatchRun {
  id: string;
  status: BatchTestStatus;
  startedAt: string;
  finishedAt: string | null;
  summary: RunSummary;
}

/** One scenario's result inside a run. Null on a scenario the run never reached. */
export interface ScenarioResult {
  id: string;
  status: BatchTestStatus;
  /** The definition AS IT RAN, snapshotted when the run started. Scenarios stay
   *  editable, so this — not the live `Scenario` — is what an old run must be
   *  rendered against. */
  scenarioName: string | null;
  scenarioQuestion: string;
  scenarioFollowUps: string[] | null;
  scenarioAssertions: ScenarioAssertion[] | null;
  scenarioMode: ScenarioMode;
  scenarioPersona: ScenarioPersona | null;
  scenarioMaxTurns: number;
  scenarioExpectation: string | null;
  /** Who the caller WAS for this run. */
  scenarioCustomer: ScenarioCustomer | null;
  /** The identity this run actually arrived with. Null = a minted test identity. */
  scenarioCaller: ScenarioCaller | null;
  conversationId: string | null;
  transcript: TranscriptTurn[] | null;
  /** Iris' reply on the last turn. */
  answer: string | null;
  answered: boolean;
  sources: unknown;
  toolsCalled: string[] | null;
  escalated: boolean;
  turnsRun: number;
  errorMessage: string | null;
  assertionResults: AssertionResult[] | null;
  /** The judge's own call, separate from the assertions so you can see WHICH
   *  kind of check failed. Null when no expectation was declared. */
  judgeVerdict: ScenarioVerdict | null;
  judgeReason: string | null;
  /** Overall: FAIL if either layer failed, PASS if something graded and nothing
   *  failed, UNGRADED if neither was declared. */
  verdict: ScenarioVerdict;
  rating: ItemRating;
  rootCause: RootCause | null;
  note: string | null;
}

/**
 * A customer seeded in CRM before a scenario's first turn.
 *
 * Scenarios otherwise run on a throwaway phone, so the caller is always a
 * stranger and the phone-keyed lookups (membership, balance, appointments)
 * have nothing to resolve. Seeded per run and removed afterwards.
 */
export interface ScenarioCustomer {
  name?: string;
  email?: string;
  /** Full service address: street, city, state, ZIP. */
  address?: string;
  /** Enrol them on a plan, so a membership question has something to read. */
  membership?: { planId: string };
}

/** The channels a scenario can arrive on. Mirrors the engine's own `channel`. */
export const SCENARIO_CHANNELS = ['sms', 'voice', 'chat', 'email'] as const;
export type ScenarioChannel = (typeof SCENARIO_CHANNELS)[number];

/**
 * The identity a scenario's turns arrive with — what Iris RECEIVES on a
 * channel, not a contact reference.
 *
 * The engine has one identity slot and every channel fills it with its own
 * source identifier: E.164 for SMS/voice, the sender's address for email, the
 * visitor id for web chat. Hand a scenario a real one and Iris runs exactly the
 * lookup she runs in production, reaching the real customer and their real
 * history — rather than us telling her who the caller is.
 *
 * Mutually exclusive with {@link ScenarioCustomer}: that one fabricates a
 * caller, this one names an identity that already exists.
 */
export interface ScenarioCaller {
  channel: ScenarioChannel;
  identifier: string;
}

/** A scenario definition, plus its result in the run being viewed. */
export interface Scenario {
  id: string;
  /** Operator-facing label; falls back to the opening message in the UI. */
  name: string | null;
  /** Turn 1 — what the customer opens with. */
  question: string;
  /** Turns 2..N, sent in order once Iris answers the previous one. */
  followUps: string[];
  assertions: ScenarioAssertion[];
  mode: ScenarioMode;
  persona: ScenarioPersona | null;
  maxTurns: number;
  /** Plain-English criteria for the judge. Null = assertions decide alone. */
  expectation: string | null;
  /** Seed a known caller before turn 1. Null = the caller is a stranger. */
  customer: ScenarioCustomer | null;
  /** Arrive as an identity that already exists. Null = a minted test identity. */
  caller: ScenarioCaller | null;
  position: number;
  result: ScenarioResult | null;
}

/** A suite as the list endpoint returns it. */
export interface BatchTest {
  id: string;
  name: string;
  /** Status of the most recent run. */
  status: BatchTestStatus;
  scenarioCount: number;
  runCount: number;
  latestRun: BatchRun | null;
  createdAt: string;
  updatedAt: string;
}

/** A suite with its scenarios and one run's results. */
export interface BatchTestDetail {
  id: string;
  name: string;
  status: BatchTestStatus;
  scenarioCount: number;
  createdAt: string;
  updatedAt: string;
  /** The run being viewed — latest unless a `runId` was requested. Null before
   *  the suite has ever been run. */
  run: BatchRun | null;
  scenarios: Scenario[];
}

/** Ack returned by `run` — the replay is detached, so this is the run handle,
 *  not the results. Poll the detail endpoint for progress. */
export interface BatchTestRunAck {
  id: string;
  batchRunId: string;
  status: BatchTestStatus;
  queued: number;
}

// ── Payloads ─────────────────────────────────────────────────────────────────

/** A scenario as sent to the API. */
export interface ScenarioPayload {
  name?: string;
  question: string;
  followUps?: string[];
  assertions?: ScenarioAssertion[];
  mode?: ScenarioMode;
  persona?: ScenarioPersona;
  maxTurns?: number;
  expectation?: string;
  customer?: ScenarioCustomer | null;
  caller?: ScenarioCaller | null;
}

/**
 * Create a suite.
 * - `recent` mines distinct recent questions (up to `limit`), one 1-turn scenario each.
 * - `conversations` lifts whole past conversations into multi-turn scenarios.
 * - `manual` uses `scenarios` (full scripts) or `questions` (1-turn shorthand).
 */
export interface CreateBatchTestPayload {
  name: string;
  source: BatchSource;
  questions?: string[];
  scenarios?: ScenarioPayload[];
  limit?: number;
}

/** Grade one scenario run. `rootCause` is required by the API when `POOR`. */
export interface RateScenarioRunPayload {
  batchId: string;
  scenarioRunId: string;
  rating: Exclude<ItemRating, 'UNRATED'>;
  rootCause?: RootCause;
  note?: string;
}

// ── Query keys ───────────────────────────────────────────────────────────────

const batchRoot = ['messaging', 'iris-batch-tests'] as const;
export const batchTestsKey = [...batchRoot, 'list'] as const;
/** The detail query for one view of a suite: the latest run, or a pinned run. */
export const batchTestKey = (id: string, runId?: string) =>
  [...batchRoot, 'detail', id, runId ?? 'latest'] as const;
/**
 * Every detail view of a suite, whichever run is pinned.
 *
 * Invalidation is prefix-matched, and `batchTestKey(id)` ends in `'latest'` —
 * so it does NOT match a view pinned to `runId`. Mutations must invalidate this
 * prefix instead, or an operator looking at an older run never sees their own
 * edit land.
 */
export const batchDetailPrefixKey = (id: string) => [...batchRoot, 'detail', id] as const;
export const batchRunsKey = (id: string) => [...batchRoot, 'runs', id] as const;

// ── Hooks ────────────────────────────────────────────────────────────────────

/** List the tenant's suites, newest first. */
export function useBatchTests() {
  return useQuery({
    queryKey: batchTestsKey,
    queryFn: () => apiGet<BatchTest[]>(operationsClient, BASE),
    staleTime: 15_000,
    // Keep the list's status badges live while any suite is replaying.
    refetchInterval: (query) =>
      query.state.data?.some((b) => b.status === 'RUNNING') ? 5_000 : false,
  });
}

/** A suite with its scenarios and one run's results (latest unless `runId`). */
export function useBatchTest(id: string | null, runId?: string) {
  return useQuery({
    queryKey: batchTestKey(id ?? '', runId),
    enabled: !!id,
    queryFn: () => {
      const query = runId ? `?runId=${encodeURIComponent(runId)}` : '';
      return apiGet<BatchTestDetail>(
        operationsClient,
        `${BASE}/${encodeURIComponent(id as string)}${query}`,
      );
    },
    // While a replay is in flight, poll so transcripts and verdicts fill in
    // live. Multi-turn scenarios take a while, so this is a slower poll than
    // the old single-question harness needed.
    //
    // Keyed on the SUITE's status, not the viewed run's: an operator can pin
    // the picker to an older completed run while a new replay is running, and
    // that view still needs to know the suite is busy.
    refetchInterval: (query) => (query.state.data?.status === 'RUNNING' ? 3_000 : false),
  });
}

/**
 * Run history for a suite, newest first — the regression trail.
 *
 * `isRunning` comes from the detail query. Without it the picker's per-run
 * tallies freeze at whatever they were when the run started, because nothing
 * invalidates this query when the run finishes.
 */
export function useBatchRuns(id: string | null, isRunning = false) {
  return useQuery({
    queryKey: batchRunsKey(id ?? ''),
    enabled: !!id,
    queryFn: () =>
      apiGet<BatchRun[]>(operationsClient, `${BASE}/${encodeURIComponent(id as string)}/runs`),
    refetchInterval: isRunning ? 3_000 : false,
  });
}

/** Create a new suite. */
export function useCreateBatchTest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateBatchTestPayload) =>
      apiPost<{ id: string }, CreateBatchTestPayload>(operationsClient, BASE, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: batchTestsKey });
    },
  });
}

/** Delete a suite, its scenarios and its run history. */
export function useDeleteBatchTest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (batchId: string) =>
      apiDelete<{ deleted: boolean }>(operationsClient, `${BASE}/${encodeURIComponent(batchId)}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: batchTestsKey });
    },
  });
}

/**
 * Start a new run of ONE scenario, leaving the rest of the suite alone.
 *
 * Iterating on a single script or persona should not mean replaying every
 * other conversation in the suite to see the one that changed. The result is
 * still a first-class run, so it shows up in the same history and can be
 * compared with a full one.
 */
export function useRunScenario() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ batchId, itemId }: { batchId: string; itemId: string }) =>
      apiPost<BatchTestRunAck, Record<string, never>>(
        operationsClient,
        `${BASE}/${encodeURIComponent(batchId)}/scenarios/${encodeURIComponent(itemId)}/run`,
        {},
      ),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: batchTestsKey });
      // The PREFIX key: `batchTestKey(id)` ends in 'latest' and would not reach
      // a view pinned to a specific run.
      void qc.invalidateQueries({ queryKey: batchDetailPrefixKey(data.id) });
      void qc.invalidateQueries({ queryKey: batchRunsKey(data.id) });
    },
  });
}

/** Start a new run of every scenario in the suite, one at a time. */
export function useRunBatchTest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (batchId: string) =>
      apiPost<BatchTestRunAck, Record<string, never>>(
        operationsClient,
        `${BASE}/${encodeURIComponent(batchId)}/run`,
        {},
      ),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: batchTestsKey });
      void qc.invalidateQueries({ queryKey: batchDetailPrefixKey(data.id) });
      void qc.invalidateQueries({ queryKey: batchRunsKey(data.id) });
    },
  });
}

/** Append a scenario to a suite. */
export function useAddScenario() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ batchId, ...body }: ScenarioPayload & { batchId: string }) =>
      apiPost<Scenario, ScenarioPayload>(
        operationsClient,
        `${BASE}/${encodeURIComponent(batchId)}/scenarios`,
        body,
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: batchDetailPrefixKey(vars.batchId) });
      void qc.invalidateQueries({ queryKey: batchTestsKey });
    },
  });
}

/** Edit a scenario's name, turns or assertions. */
export function useUpdateScenario() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      batchId: _batchId,
      scenarioId,
      ...body
    }: Partial<ScenarioPayload> & { batchId: string; scenarioId: string }) =>
      apiPut<Scenario, Partial<ScenarioPayload>>(
        operationsClient,
        `${BASE}/scenarios/${encodeURIComponent(scenarioId)}`,
        body,
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: batchDetailPrefixKey(vars.batchId) });
    },
  });
}

/** Delete a scenario and its run history. */
export function useDeleteScenario() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ scenarioId }: { batchId: string; scenarioId: string }) =>
      apiDelete<{ deleted: boolean }>(
        operationsClient,
        `${BASE}/scenarios/${encodeURIComponent(scenarioId)}`,
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: batchDetailPrefixKey(vars.batchId) });
      void qc.invalidateQueries({ queryKey: batchTestsKey });
    },
  });
}

/** Grade one scenario run Good / Acceptable / Poor (Poor requires a root cause). */
export function useRateScenarioRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ batchId: _batchId, scenarioRunId, ...body }: RateScenarioRunPayload) =>
      // The rate route is keyed on the scenario RUN id — a rating belongs to the
      // answer that was actually produced, not to the scenario definition.
      apiPut<ScenarioResult, Omit<RateScenarioRunPayload, 'batchId' | 'scenarioRunId'>>(
        operationsClient,
        `${BASE}/scenario-runs/${encodeURIComponent(scenarioRunId)}/rate`,
        body,
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: batchDetailPrefixKey(vars.batchId) });
      void qc.invalidateQueries({ queryKey: batchTestsKey });
    },
  });
}
